import { forwardRef, Ref } from 'react'
import * as Select from '@radix-ui/react-select'
import { ChevronDownIcon, XIcon } from 'lucide-react'

import { cn } from '@/lib/utils'

interface CustomSelectProps {
  options: { value: string; text: string }[]
  placeholder?: string
  value?: string
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void
  onReset?: () => void
  error?: boolean
  disabled?: boolean
  resetKey?: string | number
}

const CustomSelect = forwardRef(
  (
    {
      options,
      placeholder,
      value,
      onChange,
      onReset,
      error,
      disabled,
      resetKey
    }: CustomSelectProps,
    ref: Ref<HTMLButtonElement>
  ) => {
    const handleValueChange = (newValue: string) => {
      onChange?.(newValue)
    }

    const handleReset = (e: React.MouseEvent) => {
      e.stopPropagation()
      onReset?.()
    }

    return (
      <Select.Root
        key={resetKey}
        value={value}
        onValueChange={handleValueChange}
        disabled={disabled}
      >
        <Select.Trigger
          ref={ref}
          className={cn(
            'flex w-full appearance-none justify-between bg-transparent outline-none',
            'font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
            'rounded-[0.5rem] border border-dark/10 px-[1.5rem] py-[1.65rem] placeholder:text-dark dark:border-white/20 dark:text-white dark:placeholder:text-white',
            'focus:border-dark/40 focus:ring-2 focus:ring-dark/20 dark:focus:border-white/40 dark:focus:ring-white/20',
            {
              'border-red-500 focus:border-red-500 focus:ring-red-500/50': error,
              'dark:border-red-500 dark:focus:border-red-500 dark:focus:ring-red-500/50': error,
              'pointer-events-none opacity-40': disabled
            }
          )}
        >
          <Select.Value placeholder={placeholder} />
          <div className="flex items-center space-x-2">
            {value && onReset && (
              <button
                type="button"
                onClick={handleReset}
                className="text-dark-dark/50 hover:text-dark-dark"
              >
                <XIcon size={13} />
              </button>
            )}
            <Select.Icon>
              <ChevronDownIcon size={15} />
            </Select.Icon>
          </div>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className={cn(
              'z-10 w-full bg-white',
              'rounded-[0.5rem] border border-black/10',
              'w-[var(--radix-select-trigger-width)]'
            )}
          >
            <Select.Viewport className="p-1">
              {options.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    'font-helvetica text-[1.2rem] leading-[120%] tracking-[0.036rem]',
                    'cursor-pointer rounded-[0.5rem] px-[1.5rem] py-[1.2rem] outline-black/20',
                    'data-[highlighted]:bg-black/0 data-[state=checked]:bg-black/5'
                  )}
                >
                  <Select.ItemText>{option.text}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
    )
  }
)

CustomSelect.displayName = 'CustomSelect'

export default CustomSelect
